import { useState } from 'react'
import { ProTable } from '@ant-design/pro-components'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Alert, Button, Empty, Tag } from 'antd'
import dayjs from 'dayjs'
import { DEFAULT_PAGE_SEARCH } from '@/const/list.ts'
import { RANGE_PRESETS } from '@/const/range-presets.ts'
import { usePageTableNavigation } from '@/hooks/index.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getCreatedAtSearch, getDateRangeFormValue, type DateRangeFormValue } from '@/utils/list-query.ts'
import { MONEY_KIND_COLORS, MONEY_KIND_LABELS, MONEY_STATUS_COLORS, MONEY_STATUS_LABELS } from './const/bill-search.ts'
import type { FinanceListProps, FinanceListSearch } from './finance-search.ts'
import { useBillFilterOptions } from './hooks/use-bill-filter-options.ts'
import { MoneyDetail } from './money-detail.tsx'

export function MoneyLedger({ canManage, search, setSearch }: FinanceListProps) {
  const trpc = useTRPC(),
    [id, setId] = useState<number>()
  const query = useQuery({ ...trpc.bill.moneyList.queryOptions(search), placeholderData: keepPreviousData })
  const filterOptions = useBillFilterOptions(search, 'money.read')
  const table = usePageTableNavigation<
    FinanceListSearch,
    Partial<FinanceListSearch> & { occurredRange?: DateRangeFormValue }
  >({
    search,
    defaultSearch: DEFAULT_PAGE_SEARCH,
    navigate: ({ search: next }) => setSearch(typeof next === 'function' ? next(search) : next),
    getFormInitialValues: (value) => ({
      keyword: value.keyword ?? (value.id ? String(value.id) : undefined),
      note: value.note,
      organizationId: value.organizationId,
      status: value.status,
      kind: value.kind,
      occurredRange: getDateRangeFormValue(
        value.occurredFrom,
        value.occurredTo ? dayjs(value.occurredTo).subtract(1, 'millisecond').toISOString() : undefined
      )
    }),
    getSearchFromForm: (value) => {
      const range = getCreatedAtSearch(value.occurredRange)
      return {
        id: undefined,
        month: undefined,
        keyword: value.keyword?.trim() || undefined,
        note: value.note?.trim() || undefined,
        organizationId: value.organizationId,
        status: value.status,
        kind: value.kind,
        occurredFrom: range.createdFrom,
        occurredTo: range.createdTo ? dayjs(range.createdTo).add(1, 'millisecond').toISOString() : undefined
      }
    },
    showTotal: (total) => `共 ${total} 条`
  })
  return (
    <>
      {query.isError && <Alert type="error" title={query.error.message} />}
      <ProTable
        headerTitle={
          <div className="flex items-center gap-2">
            <span>流水明细</span>
            {query.data && (
              <span className="bg-surface-muted text-content-muted rounded px-2 py-0.5 text-xs font-normal tabular-nums">
                {query.data.total} 笔
              </span>
            )}
          </div>
        }
        size="small"
        search={{
          labelWidth: 'auto',
          defaultFormItemsNumber: 2,
          collapseRender: (collapsed) => <span className="whitespace-nowrap">{collapsed ? '展开' : '收起'}</span>
        }}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无符合条件的资金流水" /> }}
        rowKey="id"
        formRef={table.formRef}
        form={{ initialValues: table.formInitialValues }}
        columnsState={{ persistenceKey: 'finance:money', persistenceType: 'localStorage' }}
        scroll={{ x: 'max-content' }}
        dataSource={query.data?.data ?? []}
        loading={query.isFetching}
        columns={[
          {
            title: '发生时间',
            dataIndex: 'occurredRange',
            order: 90,
            hideInTable: true,
            fieldProps: { presets: RANGE_PRESETS },
            valueType: 'dateRange'
          },
          {
            title: 'ID',
            search: false,
            dataIndex: 'id',
            copyable: true,
            valueType: 'digit',
            width: '5.5rem',
            fieldProps: { min: 1, precision: 0, placeholder: '输入 ID' }
          },
          {
            title: '关键词 / ID',
            dataIndex: 'keyword',
            hideInTable: true,
            order: 100,
            fieldProps: { placeholder: '流水号或 ID', allowClear: true }
          },
          {
            title: '经营主体',
            dataIndex: 'organizationId',
            hideInTable: true,
            valueType: 'select',
            fieldProps: { ...filterOptions.organization, placeholder: '搜索经营主体', allowClear: true }
          },
          { title: '流水号', dataIndex: 'transactionNo', width: '15rem', search: false, copyable: true },
          {
            title: '类型',
            dataIndex: 'kind',
            width: '8rem',
            valueEnum: MONEY_KIND_LABELS,
            render: (_, row) => <Tag color={MONEY_KIND_COLORS[row.kind]}>{MONEY_KIND_LABELS[row.kind]}</Tag>
          },
          {
            title: '状态',
            dataIndex: 'status',
            width: '7rem',
            valueEnum: MONEY_STATUS_LABELS,
            render: (_, row) => <Tag color={MONEY_STATUS_COLORS[row.status]}>{MONEY_STATUS_LABELS[row.status]}</Tag>
          },
          { title: '主体', dataIndex: 'organizationName', width: '14rem', ellipsis: true, search: false },
          { title: '结算对象', dataIndex: 'counterpartyName', width: '12rem', ellipsis: true, search: false },
          {
            title: '金额',
            dataIndex: 'amount',
            width: '10rem',
            align: 'right',
            valueType: 'money',
            search: false,
            render: (value) => <span className="font-semibold tabular-nums">{value}</span>
          },
          {
            title: '备注',
            dataIndex: 'note',
            width: '16rem',
            ellipsis: true,
            fieldProps: { placeholder: '输入备注关键词', allowClear: true }
          },
          { title: '实际发生时间', dataIndex: 'occurredAt', width: '11rem', valueType: 'dateTime', search: false },
          { title: '登记时间', dataIndex: 'createdAt', width: '11rem', valueType: 'dateTime', search: false },
          {
            align: 'center',
            title: '操作',
            valueType: 'option',
            fixed: 'right',
            width: '5.5rem',
            render: (_, r) => (
              <Button type="link" onClick={() => setId(r.id)}>
                查看
              </Button>
            )
          }
        ]}
        onSubmit={table.onSubmit}
        onReset={table.onReset}
        pagination={{ ...table.pagination, total: query.data?.total }}
        options={{ reload: () => void query.refetch() }}
      />
      {id && <MoneyDetail key={id} id={id} canManage={canManage} onClose={() => setId(undefined)} />}
    </>
  )
}
